import { useAuth0 } from '@auth0/auth0-react';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { CircleSpinner } from 'react-spinners-kit';
import { toast } from 'react-toastify';
import Tooltip from 'rc-tooltip';
import { FiInfo } from 'react-icons/fi';
import { images } from '../../assets/images';
import { BellSvg } from '../../assets/svg-icon-components/BellSvg';
import { LOGOUT_ACTION_SESSION_CODE_PARAM } from '../../model/store';
import { RhButton, RhButtonMode } from './RhButton';
import { RhDropdown } from './RhDropdown';
import { useRequest } from '../../util/use-request';
import { colors, H5 } from './Styles';
import { apiConfigs, StudyVisibilityPayload } from '../../api/APIConfigs';
import { Church } from '../../api/models/Church';
import { useUserRoles } from '../../util/use-user-role';
import { RhConfirmModal } from './RhConfirmModal';
import {
  Border,
  ChurchBadge,
  Container,
  Flexed,
  Image,
  InfoButton,
  Knob,
  Logo,
  NotificationDiv,
  StudyVisibilityWrap,
  Tick,
  Toggle,
  ToggleLabel,
  TooltipText,
} from './Header.styles';

export const Header: React.FC<{}> = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const request = useRequest();
  const { roles } = useUserRoles();
  const [churchName, setChurchName] = useState<string>('');
  const [isAllStudyVisible, setIsAllStudyVisible] = useState(false);
  const [loadingStudyVisibility, setLoadingStudyVisibility] = useState(false);

  const loadChurches = useCallback(async () => {
    try {
      const userObject = JSON.parse(JSON.stringify(user));
      const churchKey: string | undefined = userObject['https:///churchkey'];
      const churchId: string | undefined = userObject['https:///churchid'];

      if (!churchId) {
        const churchNameSplit = (churchKey || 'SYSADMIN').split('.');
        const churchFilter = churchNameSplit.length > 1 ? churchNameSplit[1] : churchNameSplit[0];
        setChurchName(churchFilter || 'SYSADMIN');
        return;
      }

      await getAccessTokenSilently();
      const church = await request<Church>(apiConfigs.getChurchById(churchId));

      if (church) {
        setChurchName(church.name);
      } else {
        const churchNameSplit = (churchKey || 'SYSADMIN').split('.');
        const churchFilter = churchNameSplit.length > 1 ? churchNameSplit[1] : churchNameSplit[0];
        setChurchName(churchFilter);
      }
    } catch (e) {
      setChurchName('SYSADMIN');
    }
  }, [getAccessTokenSilently, request, user]);

  const loadStudyVisibility = useCallback(async () => {
    if (roles?.[0] !== 'System Admin') return;
    try {
      setLoadingStudyVisibility(true);
      const result = await request<{ id: string; isAllStudyVisible: boolean }>(apiConfigs.getStudyVisibility());
      setIsAllStudyVisible(!!result?.isAllStudyVisible);
    } catch (e: any) {
      toast.error(e.message || 'Failed to load study visibility');
    } finally {
      setLoadingStudyVisibility(false);
    }
  }, [request, roles]);

  useEffect(() => {
    loadChurches();
  }, [loadChurches]);

  useEffect(() => {
    loadStudyVisibility();
  }, [loadStudyVisibility]);

  return (
    <Container>
      <Flexed>
        <Logo src={images.risenHopeLogo} />
      </Flexed>
      <Flexed>
        {/* Implement once API supports notification */}
        {/* <NotificationDiv>
          <BellSvg width={20} height={20} />
          <Tick>1</Tick>
        </NotificationDiv> */}
        <Border>
          {roles?.[0] === 'System Admin' && (
            <StudyVisibilityWrap>
              <ToggleLabel>All studies</ToggleLabel>
              <Tooltip
                placement="bottom"
                overlay={
                  <TooltipText color={colors.background} weight="600">
                    When enabled, studiers can view studies across all churches. When disabled, studiers only see
                    studies for their church.
                  </TooltipText>
                }>
                <InfoButton aria-label="About All studies visibility" type="button">
                  <FiInfo size={14} color={colors.subtle} />
                </InfoButton>
              </Tooltip>
              {loadingStudyVisibility && <CircleSpinner size={14} color={colors.primary} />}
              <Toggle
                type="button"
                aria-label="Toggle study visibility (all studies)"
                disabled={loadingStudyVisibility}
                pressed={isAllStudyVisible}
                onClick={() => {
                  const nextValue = !isAllStudyVisible;
                  const title = nextValue
                    ? 'Enable visibility for all studies?'
                    : 'Disable visibility for all studies?';
                  const description = nextValue
                    ? 'If you enable this, studiers will be able to see all studies across all churches.'
                    : 'If you disable this, studiers will only be able to see studies for their church.';

                  confirmAlert({
                    customUI: options => (
                      <RhConfirmModal
                        isOpen={true}
                        description={`${title} ${description}`}
                        onClose={options.onClose}
                        onOk={async () => {
                          options.onClose();
                          try {
                            setLoadingStudyVisibility(true);
                            const payload: StudyVisibilityPayload = { IsAllStudyVisible: nextValue };
                            await request(apiConfigs.putStudyVisibility(payload));
                            setIsAllStudyVisible(nextValue);
                            toast.success(nextValue ? 'Study visibility enabled.' : 'Study visibility disabled.');
                          } catch (e: any) {
                            toast.error(e.message || 'Failed to update study visibility');
                          } finally {
                            setLoadingStudyVisibility(false);
                          }
                        }}
                      />
                    ),
                  });
                }}>
                <Knob pressed={isAllStudyVisible} />
              </Toggle>
            </StudyVisibilityWrap>
          )}

          <H5>
            <ChurchBadge>{churchName}</ChurchBadge>Hi, {_.startCase(user?.nickname || '').split(' ')[0] || 'user'}!
          </H5>
          <RhDropdown trigger={<Image src={user?.picture} />}>
            <RhButton
              mode={RhButtonMode.text}
              title="Log out"
              onClick={() => {
                const returnTo = `${window.location.origin}?session=${LOGOUT_ACTION_SESSION_CODE_PARAM}`;
                logout({ returnTo });
              }}
            />
          </RhDropdown>
        </Border>
      </Flexed>
    </Container>
  );
};
